// adminApi.js
// Facilitating admin level updates to the server such as updating an employee's password or permission level.

import axios from "axios";
import { getCsrfToken, setupCsrfInterceptor } from "../auth/csrfUtils";

const api = axios.create({
  baseURL: "/api",
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  // Session cookies are required for this API
  withCredentials: true,
});

// Setup CSRF protection for this API instance
setupCsrfInterceptor(api);

const baseAdminUrl = "/admin";

// testing this out

// Employee Position updates
export const updateEmployeePosition = async (positionId, status) => {
  const response = await api.post(`${baseAdminUrl}/update_employee_position`, {
    position_id: positionId,
    status: status, // 1 for active, 0 for inactive
  });
  return response.data;
};

export const addEmployeePosition = async (employeePositionDescription) => {
  const response = await api.post(`${baseAdminUrl}/add_employee_position`, {
    employee_position_description: employeePositionDescription,
  });
  return response.data;
};

// Employee Updates
export const searchEmployee = async (employeeId) => {
  const response = await api.get(
    `${baseAdminUrl}/search_employee/${employeeId}`,
  );
  return response.data;
};

export const changePassword = async (employeeId, newPassword) => {
  const response = await api.post(`${baseAdminUrl}/admin_password_change`, {
    employee_id: employeeId,
    new_password: newPassword,
  });
  return response.data;
};

export const changeUserStatus = async (employeeId, userStatus, days) => {
  const response = await api.post(`${baseAdminUrl}/change_emp_status`, {
    employee_id: employeeId,
    status: userStatus,
    days: days,
  });
  return response.data;
};

export const getAdminUsers = async () => {
  const response = await api.get(`${baseAdminUrl}/get_admins`);
  return response.data;
};

export const makeAdmin = async (employeeId) => {
  const response = await api.post(`${baseAdminUrl}/make_admin`, {
    employee_id: employeeId,
  });
  return response.data;
};

export const removeAdmin = async (employeeId) => {
  const response = await api.post(`${baseAdminUrl}/remove_admin`, {
    employee_id: employeeId,
  });
  return response.data;
};

export const addSafetyTeamMember = async (employeeId) => {
  const response = await api.post(`${baseAdminUrl}/add_safety_team_member`, {
    employee_id: employeeId,
  });
  return response.data;
};

export const removeSafetyTeamMember = async (employeeId) => {
  const response = await api.post(`${baseAdminUrl}/remove_safety_team_member`, {
    employee_id: employeeId,
  });
  return response.data;
};

export const getSafetyTeamMembers = async () => {
  const response = await api.get(`${baseAdminUrl}/get_safety_team_members`);
  return response.data;
};

export const addWasteDisposalTeamMember = async (employeeId) => {
  const response = await api.post(`${baseAdminUrl}/add_waste_disposal_team_member`, {
    employee_id: employeeId,
  });
  return response.data;
};

export const removeWasteDisposalTeamMember = async (employeeId) => {
  const response = await api.post(`${baseAdminUrl}/remove_waste_disposal_team_member`, {
    employee_id: employeeId,
  });
  return response.data;
};

export const getWasteDisposalTeamMembers = async () => {
  const response = await api.get(`${baseAdminUrl}/get_waste_disposal_team_members`);
  return response.data;
};

// Project Specific Units
export const getProjectUnitTableNames = async () => {
  const response = await api.get(
    `${baseAdminUrl}/project_specific_units/tables`,
  );
  return response.data;
};

export const downloadProjectUnitTemplate = async (tableName) => {
  const response = await api.get(
    `${baseAdminUrl}/project_specific_units/download/${tableName}`,
    { responseType: "blob" },
  );
  return response;
};

export const downloadBlankProjectUnitTemplate = async () => {
  const response = await api.get(
    `${baseAdminUrl}/project_specific_units/download/blank`,
    { responseType: "blob" },
  );
  return response;
};

export const uploadProjectUnits = async (file) => {
  const formData = new FormData();
  formData.append("projectSpecificUnitsFile", file);
  const response = await api.post(
    `${baseAdminUrl}/project_specific_units/upload`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return response.data;
};
